// @flow

import * as React from 'react';
import { Box, Flex } from 'react-system';
import { useCombobox } from 'downshift';
import {
  FormControl,
  InputAdornment,
  List,
  ListItem,
  Select,
} from '@material-ui/core';
import { Divider } from '@material-ui/core';
import { useTheme } from '../../../theme';
import { useLocale } from '../../../controls/locale';
import { useViewportType } from '../../../hooks/viewport-type';
import { useFilterContext } from '../../../controls/Filter';
import { InputPopup } from '../../../controls/popup';
import { NumberInput } from '../../../controls/number-input';
import { useDebounce } from '../../../hooks/debounce';
import { Clear } from './Clear';

type SuggestionsConstraints = {|
  minValue: number,
  maxValue: number,
  ranges: {|
    min: $ReadOnlyArray<{| from: number, to: number, step: number |}>,
    max: $ReadOnlyArray<{| from: number, to: number, step: number |}>,
  |},
|};

type SuggestionsOptions = {|
  format?: number => string,
  toInput?: mixed => string,
  fromInput?: string => mixed,
  formatPostfix?: React.Node | string,
  formatPrefix?: React.Node | string,
  constraints?: SuggestionsConstraints,
  count?: number,
  anyTitle?: string,
|};

type Props = {|
  autoFocus?: boolean,
  title: null | string,
  value: {|
    min: null | number,
    max: null | number,
  |},
  onChange: ({| min: null | number, max: null | number |}) => void,
  onClear: () => void,
  onFlush: () => void,
  onDone?: () => void,
  suggestionsOptions?: SuggestionsOptions,
  DesktopInputComponent?: React.AbstractComponent<any>,
  desktopDropdownVariant?: 'inline' | 'popup',
|};

const DEFAULT_SUGGESTIONS_COUNT = 10;

const stringOfNumber = (number: ?number) =>
  number != null ? String(number) : '';

const numberOfString = (string: string) => {
  const parsed = Number.parseFloat(string);
  if (Number.isNaN(parsed)) {
    return null;
  }
  return parsed;
};

const getSuggestions = (
  value: {|
    min: null | number,
    max: null | number,
  |},
  suggestionsConstraints: SuggestionsConstraints,
  suggestionsCount: null | number,
): {|
  min: $ReadOnlyArray<number>,
  max: $ReadOnlyArray<number>,
|} => {
  const { min: valueMin, max: valueMax } = value;
  const { minValue, maxValue, ranges } = suggestionsConstraints;
  const makeSuggestions = (suggestions, constraint) => {
    const { from, to, step } = constraint;

    if (minValue < to && maxValue > from + step) {
      const suggestionFrom = Math.max(minValue, from);
      const suggestionTo = Math.min(maxValue, to);
      const steps = (suggestionTo - suggestionFrom) / step;

      return [
        ...(suggestions ?? []),
        ...Array.from(
          { length: steps },
          (v, index) => suggestionFrom + index * step,
        ),
      ];
    } else {
      return suggestions;
    }
  };

  const minSuggestions = ranges.min.reduce(makeSuggestions, []);
  const maxSuggestions = ranges.max.reduce(makeSuggestions, []);

  const minEndIndex =
    valueMax != null
      ? minSuggestions.findIndex(
          (current, index, all) =>
            valueMax >= current && valueMax < all[index + 1],
        )
      : suggestionsCount ?? undefined;
  const maxStartIndex =
    valueMin != null
      ? maxSuggestions.findIndex(current => current > valueMin)
      : 0;

  return {
    min: minSuggestions.slice(0, minEndIndex),
    max: maxSuggestions.slice(
      maxStartIndex,
      suggestionsCount != null
        ? maxStartIndex + suggestionsCount - 1
        : undefined,
    ),
  };
};

const useFocus = () => {
  const htmlElRef = React.useRef(null);
  const setFocus = () => {
    htmlElRef.current && htmlElRef.current.focus();
  };

  return [htmlElRef, setFocus];
};

const FilterCombobox = props => {
  const { t } = useLocale();
  const { shape } = useTheme();
  const viewportType = useViewportType();
  const {
    label,
    value,
    suggestions,
    suggestionsOptions,
    elementRef,
    autoFocus,
    placeholder,
    onChange,
    onFlush,
    onSuggestionClick,
    DesktopInputComponent = NumberInput,
    desktopDropdownVariant = 'popup',
  } = props;
  // changed after input focus and blur
  const [tmpValue, setTmpValue] = React.useState(value);
  const comboboxRoot = React.useRef<HTMLElement | null>(null);
  const [error, setError] = React.useState(null);
  const isValid = value => {
    // max value of 32 bit signed integer
    const maxInt = 2147483647;
    return value != null && value < maxInt;
  };

  const combobox = useCombobox({
    items: suggestions,
    inputValue: stringOfNumber(tmpValue),
    itemToString: item => stringOfNumber(item),
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem == null) {
        return;
      }
      onSuggestionClick(selectedItem);
      elementRef.current?.blur();
    },
  });
  const { isOpen, highlightedIndex } = combobox;
  const { getComboboxProps, openMenu, closeMenu } = combobox;
  const { getInputProps, getMenuProps, getItemProps } = combobox;
  const openMenuDebounced = useDebounce(openMenu, 800);
  const onChangeDebounced = useDebounce(onChange, 500);

  // autofocus on the next frame to prevent flickering caused by conflicts with popup
  React.useEffect(() => {
    let timeout;
    if (autoFocus === true) {
      timeout = setTimeout(() => elementRef.current?.focus());
    }
    return () => clearTimeout(timeout);
  }, [autoFocus, elementRef]);

  let dropdown = null;

  if (viewportType !== 'mobile') {
    let items = suggestions;
    if (desktopDropdownVariant === 'inline') {
      items = items.slice(0, 7); // limit by 7 to not overweight UI
    }

    items = items.map((suggestion, index) => (
      <ListItem
        {...getItemProps({ index, item: suggestion })}
        key={index}
        button={true}
        selected={highlightedIndex === index}
        css={{ borderRadius: shape.borderRadius }}
        tabIndex={-1}
      >
        {suggestionsOptions?.formatPrefix}
        {suggestionsOptions?.format
          ? suggestionsOptions.format(suggestion)
          : suggestion}
        <span css={{ marginLeft: 2 }}>{suggestionsOptions?.formatPostfix}</span>
      </ListItem>
    ));

    dropdown =
      desktopDropdownVariant === 'inline' ? (
        <List css={{ marginTop: 4 }} {...getMenuProps()}>
          {items}
        </List>
      ) : (
        <InputPopup
          referenceRef={comboboxRoot}
          open={isOpen && suggestions.length > 0}
        >
          <List {...getMenuProps()}>{isOpen && items}</List>
        </InputPopup>
      );
  }

  return (
    <FormControl variant="outlined" size="small">
      {viewportType === 'mobile' && suggestions.length ? (
        <Select
          native={true}
          value={value == null ? '' : value}
          onChange={event => {
            onChange(numberOfString(event.target.value));
          }}
          // call onFlush only if the value before and after isn't equal
          onBlur={() => {
            setTmpValue(value);
            if (tmpValue !== value) {
              onFlush();
            }
          }}
          onFocus={() => {
            openMenu();
            setTmpValue(value);
          }}
          inputProps={{
            ref: elementRef,
            'aria-label': label,
          }}
        >
          {value != null && suggestions.indexOf(value) === -1 ? (
            <option value={value}>
              {suggestionsOptions?.formatPrefix}
              {suggestionsOptions?.format
                ? suggestionsOptions.format(value)
                : value}
              {suggestionsOptions?.formatPostfix}
            </option>
          ) : null}
          <option value={null}>
            {suggestionsOptions?.anyTitle ?? t('any')}
          </option>
          {suggestions.map((suggestion, index) => (
            <option key={`suggestion-${index}`} value={suggestion}>
              {suggestionsOptions?.formatPrefix}
              {suggestionsOptions?.format
                ? suggestionsOptions.format(suggestion)
                : suggestion}
              {suggestionsOptions?.formatPostfix}
            </option>
          ))}
        </Select>
      ) : (
        <div {...getComboboxProps({ ref: comboboxRoot })}>
          <DesktopInputComponent
            placeholder={placeholder}
            endAdornment={
              suggestionsOptions?.formatPostfix != null ? (
                <InputAdornment position="end">
                  {suggestionsOptions.formatPostfix}
                </InputAdornment>
              ) : null
            }
            {...getInputProps({
              ref: elementRef,
              refKey: 'inputRef',
              // call onFlush only if the value before and after isn't equal
              onBlur: () => {
                setTmpValue(value);
                if (tmpValue !== value) {
                  onFlush();
                }
              },
              onFocus: () => {
                openMenu();
                setTmpValue(value);
              },
              onKeyDown: event => {
                // hide menu while user is typing
                if (
                  !['Escape', 'Enter', 'ArrowDown', 'ArrowUp'].includes(
                    event.key,
                  )
                ) {
                  closeMenu();
                  openMenuDebounced();
                }
              },
            })}
            onChange={formatted => {
              const isValidInteger = isValid(formatted);
              if (isValidInteger) {
                setError(null);
                setTmpValue(formatted);
                onChangeDebounced(numberOfString(formatted));
              } else {
                setError(t('errorValueTooHigh'));
              }
            }}
            error={error != null}
          />
          {error != null && (
            <div style={{ color: 'red', padding: '10px 0px 0px 5px' }}>
              {error}
            </div>
          )}
          {dropdown}
        </div>
      )}
    </FormControl>
  );
};

export const FilterMinMax = (props: Props): React.Node => {
  const { t } = useLocale();
  const { text } = useTheme();
  const viewportType = useViewportType();
  const { titleColor, subTitleColor } = useFilterContext();
  const [minRef, setMinFocus] = useFocus();
  const [maxRef, setMaxFocus] = useFocus();
  const {
    autoFocus,
    title,
    value,
    onChange,
    onFlush,
    onClear,
    onDone,
    suggestionsOptions,
    DesktopInputComponent,
    desktopDropdownVariant,
  } = props;

  let suggestions = {
    min: [],
    max: [],
  };

  if (suggestionsOptions?.constraints != null) {
    suggestions = getSuggestions(
      value,
      suggestionsOptions.constraints,
      viewportType === 'mobile'
        ? null
        : suggestionsOptions.count ?? DEFAULT_SUGGESTIONS_COUNT,
    );
  }

  return (
    <>
      <Flex justifyContent="space-between" alignItems="baseline" pt={0} pb={2}>
        {title != null && (
          <Box py={1} pb={2} css={[text.subtitle1, { color: titleColor }]}>
            {title}
          </Box>
        )}
        <Clear
          css={{ color: subTitleColor }}
          onClick={() => {
            onClear();
            setMinFocus();
          }}
        >
          {t('clear')}
        </Clear>
      </Flex>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: '1fr 16px 1fr',
          gridGap: '12px',
        }}
      >
        <FilterCombobox
          DesktopInputComponent={DesktopInputComponent}
          label={t('min')}
          value={value.min}
          suggestions={[...suggestions.min]}
          suggestionsOptions={suggestionsOptions}
          elementRef={minRef}
          autoFocus={autoFocus}
          placeholder={t('min')}
          desktopDropdownVariant={desktopDropdownVariant}
          onFlush={onFlush}
          onChange={newValue => {
            onChange({
              min: newValue,
              max: value.max,
            });
            onFlush();
          }}
          onSuggestionClick={suggestion => {
            onChange({
              min: suggestion,
              max: value.max,
            });
            setMaxFocus();
            onFlush();
          }}
        />

        <Divider css={{ marginTop: '20px' }} />

        <FilterCombobox
          DesktopInputComponent={DesktopInputComponent}
          autoFocus={false}
          label={t('max')}
          value={value.max}
          suggestions={[...suggestions.max]}
          suggestionsOptions={suggestionsOptions}
          elementRef={maxRef}
          placeholder={t('max')}
          desktopDropdownVariant={desktopDropdownVariant}
          onFlush={onFlush}
          onChange={newValue => {
            onChange({
              min: value.min,
              max: newValue,
            });
            onFlush();
          }}
          onSuggestionClick={suggestion => {
            onChange({
              min: value.min,
              max: suggestion,
            });
            typeof onDone === 'function' && onDone();
            onFlush();
          }}
        />
      </div>
    </>
  );
};
